import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { CheckCircle, AlertCircle, Loader2, FileSpreadsheet, Eye, ChevronDown, ChevronUp } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { format } from 'date-fns'
import type { TAMUploadBatch } from '@/hooks/useAccounts'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface TAMUploadHistoryProps {
  clientId: string
}

export function TAMUploadHistory({ clientId }: TAMUploadHistoryProps) {
  const [batches, setBatches] = useState<TAMUploadBatch[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedBatch, setExpandedBatch] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] = useState<{ batchId: string; errors: any[] } | null>(null)

  useEffect(() => {
    fetchBatches()
  }, [clientId])

  const fetchBatches = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('tam_upload_batches')
        .select('*')
        .eq('client_id', clientId)
        .order('uploaded_at', { ascending: false })
        .limit(50)

      if (error) throw error

      setBatches(data || [])
    } catch (error) {
      console.error('Error fetching upload batches:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusIcon = (status: TAMUploadBatch['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'partial':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />
      case 'processing':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
      default:
        return <FileSpreadsheet className="h-4 w-4 text-gray-500" />
    }
  }

  const getStatusBadge = (status: TAMUploadBatch['status']) => {
    const variants: Record<TAMUploadBatch['status'], 'default' | 'secondary' | 'destructive'> = {
      completed: 'default',
      partial: 'secondary',
      failed: 'destructive',
      processing: 'secondary',
      uploading: 'secondary',
    }

    return (
      <Badge variant={variants[status] || 'secondary'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    )
  }

  const getUploadTypeLabel = (type: string) => {
    switch (type) {
      case 'tam_accounts':
        return 'TAM Accounts'
      case 'tam_contacts':
        return 'TAM Contacts'
      case 'blocklist':
        return 'Blocklist'
      default:
        return type
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (batches.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Upload History</CardTitle>
          <CardDescription>No uploads found for this client</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload History</CardTitle>
        <CardDescription>View past CSV uploads and processing results</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>File</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Results</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {batches.map((batch) => (
              <React.Fragment key={batch.id}>
                <TableRow>
                  <TableCell className="font-medium">{batch.filename}</TableCell>
                  <TableCell>{getUploadTypeLabel(batch.upload_type)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {format(new Date(batch.uploaded_at), 'MMM d, yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(batch.status)}
                      {getStatusBadge(batch.status)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm space-y-1">
                      <div className="flex gap-3">
                        <span className="text-green-600">✓ {batch.created_count} created</span>
                        {batch.updated_count > 0 && (
                          <span className="text-blue-600">↻ {batch.updated_count} updated</span>
                        )}
                        {batch.error_count > 0 && (
                          <span className="text-red-600">✗ {batch.error_count} errors</span>
                        )}
                        {batch.skipped_count > 0 && (
                          <span className="text-gray-600">⊘ {batch.skipped_count} skipped</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {batch.processed_rows} of {batch.total_rows} rows processed
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {batch.error_count > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setErrorDetails({
                              batchId: batch.id,
                              errors: batch.error_details || [],
                            })
                          }
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Errors
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setExpandedBatch(expandedBatch === batch.id ? null : batch.id)
                        }
                      >
                        {expandedBatch === batch.id ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {expandedBatch === batch.id && (
                  <TableRow>
                    <TableCell colSpan={6} className="bg-muted/50">
                      <div className="p-4 space-y-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <div className="text-muted-foreground">Uploaded By</div>
                            <div className="font-medium">{batch.uploaded_by}</div>
                          </div>
                          <div>
                            <div className="text-muted-foreground">Total Rows</div>
                            <div className="font-medium">{batch.total_rows}</div>
                          </div>
                          <div>
                            <div className="text-muted-foreground">Processed</div>
                            <div className="font-medium">{batch.processed_rows}</div>
                          </div>
                          <div>
                            <div className="text-muted-foreground">Success Rate</div>
                            <div className="font-medium">
                              {batch.total_rows > 0
                                ? Math.round(
                                    ((batch.created_count + batch.updated_count) /
                                      batch.total_rows) *
                                      100
                                  )
                                : 0}
                              %
                            </div>
                          </div>
                        </div>
                        {batch.error_details && batch.error_details.length > 0 && (
                          <div>
                            <div className="text-sm font-medium mb-2">Errors ({batch.error_details.length}):</div>
                            <div className="max-h-40 overflow-y-auto space-y-1">
                              {batch.error_details.slice(0, 10).map((error: any, idx: number) => (
                                <div key={idx} className="text-xs p-2 bg-red-50 rounded border border-red-200">
                                  <span className="font-medium">Row {error.row}:</span> {error.field} - {error.error}
                                </div>
                              ))}
                              {batch.error_details.length > 10 && (
                                <div className="text-xs text-muted-foreground">
                                  ... and {batch.error_details.length - 10} more errors
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>

        {/* Error Details Dialog */}
        {errorDetails && (
          <Dialog open={!!errorDetails} onOpenChange={() => setErrorDetails(null)}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Error Details</DialogTitle>
                <DialogDescription>
                  Detailed error information for upload batch
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {errorDetails.errors.map((error: any, idx: number) => (
                  <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="text-sm font-medium">
                      Row {error.row} - {error.field}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">{error.error}</div>
                    {error.value && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Value: {error.value}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  )
}



